import React from "react";
import { useState, useRef, useEffect } from "react";
import { Button } from "../../primitives/button";
import { Textarea } from "../../primitives/textarea";
import { Send, Loader2, MessageSquare } from "lucide-react";
import { getRuntimeConfig } from "@shared-core/config/runtime-config";
import { useStreamingChat } from "@shared-core/domain/useStreamingChat";
import type { ChatMessage } from "@shared-core/data/feature-session-types";

interface ChatPanelProps {
  enabled: boolean;
  repository: string;
  branchName: string;
}

// Render inline markdown: **bold**, *italic*, `code`
function renderInline(text: string): React.ReactNode[] {
  if (!text) return [];
  const result: React.ReactNode[] = [];
  const re = /\*\*([^*\n]+)\*\*|`([^`\n]+)`|\*([^*\n]+)\*/g;
  let lastIdx = 0;
  let key = 0;
  let match: RegExpExecArray | null;

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIdx) {
      result.push(text.slice(lastIdx, match.index));
    }
    if (match[1] !== undefined) {
      result.push(
        <strong key={key++} className="font-semibold">
          {match[1]}
        </strong>,
      );
    } else if (match[2] !== undefined) {
      result.push(
        <code
          key={key++}
          className="bg-black/10 dark:bg-white/10 px-1 py-0.5 rounded text-xs font-mono"
        >
          {match[2]}
        </code>,
      );
    } else if (match[3] !== undefined) {
      result.push(<em key={key++}>{match[3]}</em>);
    }
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < text.length) result.push(text.slice(lastIdx));
  return result.length ? result : [text];
}

function renderTextBlock(text: string, outerKey: number): React.ReactNode {
  const elements: React.ReactNode[] = [];
  const listItems: React.ReactNode[] = [];
  let listType: "ul" | "ol" | null = null;
  let elemKey = 0;

  function flushList() {
    if (!listItems.length) return;
    const Tag = listType === "ul" ? "ul" : "ol";
    const cls = `${listType === "ul" ? "list-disc" : "list-decimal"} pl-4 my-1 space-y-0.5`;
    elements.push(
      React.createElement(
        Tag,
        { key: `list-${elemKey++}`, className: cls },
        ...listItems,
      ),
    );
    listItems.length = 0;
    listType = null;
  }

  text.split("\n").forEach((line, lineIdx) => {
    const hMatch = line.match(/^(#{1,3}) (.*)/);
    if (hMatch) {
      flushList();
      elements.push(
        <p key={`h-${lineIdx}`} className="font-semibold mt-2 mb-0.5">
          {renderInline(hMatch[2])}
        </p>,
      );
      return;
    }

    const bulletMatch = line.match(/^[-*] (.*)/);
    if (bulletMatch) {
      if (listType === "ol") flushList();
      listType = "ul";
      listItems.push(
        <li key={`li-${lineIdx}`}>{renderInline(bulletMatch[1])}</li>,
      );
      return;
    }

    const numMatch = line.match(/^\d+\. (.*)/);
    if (numMatch) {
      if (listType === "ul") flushList();
      listType = "ol";
      listItems.push(
        <li key={`li-${lineIdx}`}>{renderInline(numMatch[1])}</li>,
      );
      return;
    }

    flushList();

    if (line.trim() === "") {
      elements.push(<br key={`br-${lineIdx}`} />);
      return;
    }

    elements.push(
      <span key={`l-${lineIdx}`} className="block">
        {renderInline(line)}
      </span>,
    );
  });

  flushList();
  return <span key={`block-${outerKey}`}>{elements}</span>;
}

// Render message content with markdown: code fences, headings, lists, bold, italic, inline code.
function renderContent(text: string): React.ReactNode {
  // Split on fenced code blocks (``` optional-lang \n ... ```)
  const segments = text.split(/(```[\w]*\n[\s\S]*?```)/);
  const parts: React.ReactNode[] = [];

  segments.forEach((segment, i) => {
    if (segment.startsWith("```")) {
      const inner = segment.slice(3, -3);
      const newline = inner.indexOf("\n");
      const lang = newline > 0 ? inner.slice(0, newline).trim() : "";
      const code = lang ? inner.slice(newline + 1) : inner;
      parts.push(
        <pre
          key={i}
          className="bg-muted rounded-md p-3 my-2 text-xs overflow-x-auto whitespace-pre"
        >
          <code>{code}</code>
        </pre>,
      );
    } else if (segment) {
      parts.push(renderTextBlock(segment, i));
    }
  });

  return <>{parts}</>;
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === "user";

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}>
      <div
        className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
          isUser
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-foreground"
        }`}
      >
        {isUser ? (
          <span className="whitespace-pre-wrap">{message.content}</span>
        ) : (
          renderContent(message.content)
        )}
        {message.isStreaming && (
          <span className="inline-block w-1.5 h-4 bg-current opacity-60 animate-pulse ml-0.5 align-middle" />
        )}
      </div>
    </div>
  );
}

export function ChatPanel({ enabled, repository, branchName }: ChatPanelProps) {
  const isMockMode = !getRuntimeConfig().apiBaseUrl;

  const { messages, isStreaming, statusText, error, sendMessage } =
    useStreamingChat({ mockMode: isMockMode, repository, branchName });

  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new messages / streaming updates
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, statusText]);

  function handleSend() {
    const text = input.trim();
    if (!text || isStreaming || !enabled) return;
    setInput("");
    sendMessage(text);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  if (!enabled) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2">
        <MessageSquare className="size-8 opacity-40" />
        <p className="text-sm">Start a session to begin chatting</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Messages area — plain div so scrollTop works and flex-1 stays bounded */}
      <div className="flex-1 min-h-0 overflow-y-auto px-3 py-2">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2 py-12">
            <MessageSquare className="size-8 opacity-40" />
            <p className="text-sm">Send a message to start developing</p>
          </div>
        )}
        {messages.map((msg) => (
          <MessageBubble key={msg.id} message={msg} />
        ))}
        {/* Sentinel element for auto-scroll */}
        <div ref={messagesEndRef} />
      </div>

      {/* Status line */}
      {(statusText || error) && (
        <div className="px-3 py-1 text-xs border-t border-border">
          {error ? (
            <span className="text-destructive">{error}</span>
          ) : (
            <span className="text-muted-foreground flex items-center gap-1">
              <Loader2 className="size-3 animate-spin" />
              {statusText}
            </span>
          )}
        </div>
      )}

      {/* Input area */}
      <div className="border-t border-border p-3 flex-shrink-0">
        <div className="flex gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe the changes you want..."
            className="min-h-[44px] max-h-[120px] resize-none"
            rows={1}
            disabled={isStreaming}
          />
          <Button
            size="icon"
            onClick={handleSend}
            disabled={!input.trim() || isStreaming}
            className="flex-shrink-0 self-end"
          >
            <Send className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
